import React, { useCallback, useReducer } from 'react';
import { Box, Typography, Stack, Button } from '@mui/material';
import EnhancedTable from '../../enhancedTable/EnhancedTable';
import { usePreferences } from '../../../hooks/usePreferences';
import { formatDateWithPreferences } from '../../../utils/dateFormatUtils';
import useAbortController from '../../../hooks/useAbortController';
import { isLocalDev } from '../../../utils/environment';
import { TimeService } from '../../../service/timeService';
import useLocation from 'hooks/useLocation';
import SimpleDatePicker from '../../ui/simpleDatePicker';

const columnsFactory = (preferences, availableLocations) => [
  {
    Header: 'Employee',
    accessor: (row) => `${row.employee?.firstName || ''} ${row.employee?.lastName || ''}`.trim(),
    id: 'employeeName',
    enableFilter: true,
    type: 'text'
  },
  {
    Header: 'Location',
    accessor: (row) => row.location?.name || '-',
    id: 'locationId',
    enableFilter: true,
    type: 'enum',
    filterOptions: availableLocations.map(loc => ({ value: loc.id, label: loc.name })),
  },
  {
    Header: 'Clock In',
    accessor: 'clockInAt',
    enableFilter: true,
    type: 'date',
    Cell: ({ value }) => formatDateWithPreferences(value, preferences)
  },
  {
    Header: 'Clock Out',
    accessor: 'clockOutAt',
    enableFilter: true,
    type: 'date',
    Cell: ({ value }) => value ? formatDateWithPreferences(value, preferences) : '-'
  },
  {
    Header: 'Duration',
    accessor: (row) => {
      const start = row.clockInAt ? new Date(row.clockInAt) : null;
      const end = row.clockOutAt ? new Date(row.clockOutAt) : new Date();
      if (!start) return '-';
      const ms = end - start;
      const hours = Math.floor(ms / 3600000);
      const minutes = Math.floor((ms % 3600000) / 60000);
      return `${hours}h ${minutes}m`;
    },
    id: 'duration'
  },
  {
    Header: 'Status',
    accessor: 'status',
    enableFilter: true,
    type: 'enum',
    filterOptions: [
      { value: 'open', label: 'Open' },
      { value: 'closed', label: 'Closed' }
    ]
  }
];

export default function TimeTrackingPage() {
  const { preferences } = usePreferences();
  const [signal, abortRequest] = useAbortController();
const { availableLocations } = useLocation();

  const [state, updateState] = useReducer((s, p) => ({ ...s, ...p }), {
    data: [],
    totalRowCount: 0,
    pageCount: 0,
    loading: false,
    start: null,
    end: null,
  });

  const fmtYMD = (d) => {
    if (!d) return null;
    const dt = (d instanceof Date) ? d : new Date(d);
    return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()).toISOString().slice(0,10);
  };

  const applyPreset = (key) => {
    const now = new Date();
    let start = null, end = null;
    if (key === 'today') {
      start = now; end = now;
    } else if (key === 'thisWeek') {
      const day = now.getDay();
      const diffToMonday = (day === 0 ? -6 : 1) - day; // Monday start
      const monday = new Date(now);
      monday.setDate(now.getDate() + diffToMonday);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      start = monday; end = sunday;
    } else if (key === 'thisMonth') {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      start = first; end = last;
    } else if (key === 'last30') {
      const past = new Date(now);
      past.setDate(now.getDate() - 30);
      start = past; end = now;
    }
    updateState({ start: fmtYMD(start), end: fmtYMD(end) });
  };

  const fetchData = useCallback(({ pageSize, pageIndex, filters, sortBy }) => {
    const from = pageSize * pageIndex;
    updateState({ loading: true });
    const range = { start: state.start, end: state.end };
    TimeService.getWithLimit(from, pageSize, filters, sortBy, signal, range)
      .then(result => {
        const { code, data } = result;
        if (code === 0) {
          updateState({
            data: data.data,
            totalRowCount: data.count,
            pageCount: Math.ceil(data.count / pageSize)
          });
        }
        updateState({ loading: false });
      })
      .catch(error => {
        if (isLocalDev) console.error(error);
        updateState({ loading: false });
      });
  }, [signal, state.start, state.end]);

  const columns = columnsFactory(preferences, availableLocations);

  return (
    <Box display="flex" flexDirection="column">
      <Typography variant="h5" sx={{ mb: 2 }}>Employee Time Tracking</Typography>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Button size="small" variant="outlined" onClick={() => applyPreset('today')}>Today</Button>
        <Button size="small" variant="outlined" onClick={() => applyPreset('thisWeek')}>This Week</Button>
        <Button size="small" variant="outlined" onClick={() => applyPreset('thisMonth')}>This Month</Button>
        <Button size="small" variant="outlined" onClick={() => applyPreset('last30')}>Last 30 Days</Button>
        <Box sx={{ width: 16 }} />
        <SimpleDatePicker
          label="Start"
          value={state.start}
          onChange={(v) => updateState({ start: v })}
          helperTextMode="never"
          size="small"
        />
        <SimpleDatePicker
          label="End"
          value={state.end}
          onChange={(v) => updateState({ end: v })}
          helperTextMode="never"
          size="small"
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button
          size="small"
          variant="contained"
          onClick={() => {
            const q = new URLSearchParams();
            if (state.start) q.set('start', state.start);
            if (state.end) q.set('end', state.end);
            const url = `/employee/time/export${q.toString() ? `?${q.toString()}` : ''}`;
            window.open(url, '_blank');
          }}
        >
          Export CSV
        </Button>
      </Stack>
      <EnhancedTable
        columns={columns}
        data={state.data}
        totalRowCount={state.totalRowCount}
        pageCount={state.pageCount}
        fetchData={fetchData}
        loading={state.loading}
        selectionMode="none"
        showStatusColumn={false}
      />
    </Box>
  );
}
